// Canonical internal threat catalog. Single source of truth — frameworks
// (modules/mappings/frameworks/) map TO this. Adding a threat here makes it
// available everywhere (heatmap, scoring, recommendations, reports).
//
// Editing rules (also enforced by `pnpm validate:taxonomy`):
//   - `id` must be globally unique snake_case
//   - every threat needs a base weight in modules/taxonomy/weights.ts for
//     every tech-footprint domain it could apply to (zero is fine)
//   - every non-operational-category threat must be referenced by at least
//     one framework mapping file under modules/mappings/mappings/ — every
//     category here is marked `operational: true` (see threat-categories.ts)
//     so this app ships v1 without an external cross-reference catalog
//   - every one of the 16 framework controls must appear in at least one
//     threat's primaryControlIds/secondaryControlIds, or relevantControlIds()
//     will never surface it as a maturity question. Verified by hand for the
//     18 threats below — see the coverage note at the end of this file.

import type { Threat } from "../shared/types";
import { categoryLabelsFrom } from "./threat-categories";

export const THREATS: Threat[] = [
  { id: "eol_infra_exploitation", name: "Exploitation of End-of-Life/Unsupported Infrastructure",
    shortLabel: "EOL/LDoS Exploitation", category: "INFRASTRUCTURE",
    description: "Attackers target infrastructure that has aged out of vendor support and will never receive another patch, regardless of how mature the org's patch operations are.",
    primaryControlIds: ["D2.SD1.C1"],
    secondaryControlIds: ["D1.SD2.C2", "D2.SD2.C1", "D2.SD2.C2", "D2.SD2.C3"] },

  { id: "machine_speed_cve", name: "Machine-Speed CVE Exploitation", shortLabel: "Machine-Speed CVE",
    category: "OPERATIONAL",
    description: "AI-accelerated attackers weaponize a newly disclosed CVE within days, compressing the window a manual, calendar-driven patch process has to close it.",
    primaryControlIds: ["D1.SD2.C1"],
    secondaryControlIds: ["D1.SD1.C1", "D1.SD3.C1", "D3.SD3.C1"] },

  { id: "unpatched_known_vuln", name: "Unpatched Known-Vulnerability Exploitation",
    shortLabel: "Unpatched Known Vuln", category: "INFRASTRUCTURE",
    description: "A previously disclosed, already-patchable vulnerability remains exploitable because deployment lagged the vendor fix.",
    primaryControlIds: ["D1.SD2.C1"], secondaryControlIds: ["D1.SD1.C1"] },

  { id: "flat_network_lateral_movement", name: "Lateral Movement via Flat/Under-Segmented Networks",
    shortLabel: "Flat-Network Lateral Movement", category: "INFRASTRUCTURE",
    description: "Once inside, an attacker moves freely between systems because the network lacks segmentation informed by real attack-path analysis.",
    primaryControlIds: ["D3.SD2.C3"], secondaryControlIds: ["D1.SD2.C2", "D3.SD1.C2"] },

  { id: "credential_phishing_token_theft", name: "Credential Phishing & Session/Token Theft",
    shortLabel: "Credential Phishing", category: "IDENTITY_AGENT",
    description: "AI-generated phishing content harvests credentials or session tokens, then reuses them to move through the environment as a trusted identity.",
    primaryControlIds: ["D3.SD2.C1"], secondaryControlIds: ["D3.SD1.C1", "D3.SD3.C1"] },

  { id: "deepfake_vishing_social_engineering", name: "Deepfake / AI-Generated Vishing & Social Engineering",
    shortLabel: "Deepfake Social Engineering", category: "IDENTITY_AGENT",
    description: "AI-generated voice or video impersonates an executive or trusted contact over collaboration channels to extract credentials, approvals, or funds.",
    primaryControlIds: ["D3.SD2.C1"], secondaryControlIds: ["D3.SD1.C1", "D3.SD3.C1"] },

  { id: "rogue_wireless_access", name: "Rogue / Unmanaged Wireless Access Points",
    shortLabel: "Rogue Wireless Access", category: "INFRASTRUCTURE",
    description: "An unmanaged or rogue access point gives an attacker a foothold that bypasses wired-network controls entirely.",
    primaryControlIds: ["D1.SD1.C1"], secondaryControlIds: ["D3.SD2.C3"] },

  { id: "sdwan_control_plane_exposure", name: "SD-WAN Control-Plane / Orchestrator Exposure",
    shortLabel: "SD-WAN Control-Plane Exposure", category: "INFRASTRUCTURE",
    description: "Compromising the SD-WAN orchestrator or controller can affect every connected site at once, making it a high-value, high-blast-radius target.",
    primaryControlIds: ["D1.SD2.C2"], secondaryControlIds: ["D3.SD2.C1"] },

  { id: "firewall_misconfig_exposure", name: "Firewall/Perimeter Misconfiguration & Rule Sprawl",
    shortLabel: "Firewall Misconfiguration", category: "INFRASTRUCTURE",
    description: "Accumulated, unaudited firewall rules and default configurations quietly widen the perimeter's actual exposure beyond its intended policy.",
    primaryControlIds: ["D1.SD2.C2"], secondaryControlIds: ["D3.SD2.C3"] },

  { id: "exposed_mgmt_interfaces", name: "Exposed Management Interfaces & Default Credentials",
    shortLabel: "Exposed Management Interfaces", category: "INFRASTRUCTURE",
    description: "Device management interfaces reachable from untrusted networks, or still running default credentials, give an attacker a direct administrative path in.",
    primaryControlIds: ["D1.SD1.C1", "D1.SD2.C2"], secondaryControlIds: ["D3.SD2.C1"] },

  { id: "agent_prompt_injection_tool_abuse", name: "AI Agent Prompt Injection & Tool Abuse",
    shortLabel: "Agent Prompt Injection", category: "IDENTITY_AGENT",
    description: "An AI agent with real tool access is manipulated via injected instructions into taking actions outside its intended scope.",
    primaryControlIds: ["D3.SD2.C4"], secondaryControlIds: ["D3.SD2.C1"] },

  { id: "agent_identity_sprawl_shadow_agents", name: "Agent Identity Sprawl / Shadow Agents",
    shortLabel: "Shadow Agents", category: "IDENTITY_AGENT",
    description: "AI agents proliferate without central inventory or identity governance, leaving ungoverned agents with standing access no one is tracking.",
    primaryControlIds: ["D3.SD1.C1"], secondaryControlIds: ["D3.SD2.C1", "D3.SD2.C4"] },

  { id: "agentic_soc_alert_fatigue", name: "SOC Alert Fatigue / Inability to Respond at Machine Speed",
    shortLabel: "SOC Alert Fatigue", category: "OPERATIONAL",
    description: "Alert volume exceeds manual analyst capacity, so genuine incidents age out unreviewed while an AI-accelerated attacker operates at machine speed.",
    primaryControlIds: ["D3.SD3.C1"], secondaryControlIds: ["D1.SD2.C1"] },

  { id: "supply_chain_oss_propagation", name: "Open-Source / Software Supply-Chain Propagation",
    shortLabel: "OSS Supply-Chain Propagation", category: "OPERATIONAL",
    description: "A vulnerability in a widely used open-source component propagates across every application that depends on it, often faster than dependency visibility can keep up.",
    primaryControlIds: ["D3.SD2.C2"], secondaryControlIds: ["D1.SD2.C1"] },

  { id: "multi_vendor_visibility_gaps", name: "Multi-Vendor Visibility & Coordination Gaps",
    shortLabel: "Multi-Vendor Visibility Gaps", category: "OPERATIONAL",
    description: "Inconsistent tooling and processes across vendors leave blind spots at the seams between Cisco and non-Cisco infrastructure.",
    primaryControlIds: ["D1.SD1.C1"], secondaryControlIds: ["D3.SD1.C2", "D3.SD3.C1"] },

  { id: "regulated_data_exposure_infra", name: "Regulated-Data Exposure via Infrastructure Compromise",
    shortLabel: "Regulated-Data Exposure", category: "OPERATIONAL",
    description: "An infrastructure-level compromise reaches systems processing regulated data, turning an operational gap into a compliance and disclosure event.",
    primaryControlIds: ["D3.SD2.C2"], secondaryControlIds: ["D3.SD2.C3"] },

  { id: "unmanaged_byod_collab_exposure", name: "Unmanaged Device / BYOD Exposure via Collaboration Tools",
    shortLabel: "BYOD Collaboration Exposure", category: "IDENTITY_AGENT",
    description: "Unmanaged personal devices accessing collaboration platforms give an attacker a path to credentials and internal conversations outside device-management controls.",
    primaryControlIds: ["D3.SD2.C1"], secondaryControlIds: ["D1.SD1.C1"] },

  { id: "cloud_onprem_hybrid_gap", name: "Inconsistent Controls Across Cloud/On-Prem Hybrid Estate",
    shortLabel: "Hybrid Estate Control Gaps", category: "OPERATIONAL",
    description: "Hardening and monitoring standards applied on-prem don't carry over consistently to cloud-hosted infrastructure (or vice versa), leaving a gap at the boundary.",
    primaryControlIds: ["D1.SD2.C2"], secondaryControlIds: ["D2.SD3.C1"] },
];

// Coverage check (all 16 controls, verified by hand): D1.SD1.C1 (rogue_wireless_access,
// exposed_mgmt_interfaces, multi_vendor_visibility_gaps primary; unpatched_known_vuln,
// unmanaged_byod_collab_exposure, machine_speed_cve secondary), D1.SD2.C1 (machine_speed_cve,
// unpatched_known_vuln primary; agentic_soc_alert_fatigue, supply_chain_oss_propagation
// secondary), D1.SD2.C2 (sdwan_control_plane_exposure, firewall_misconfig_exposure,
// exposed_mgmt_interfaces, cloud_onprem_hybrid_gap primary; eol_infra_exploitation,
// flat_network_lateral_movement secondary), D1.SD3.C1 (machine_speed_cve secondary),
// D2.SD1.C1 (eol_infra_exploitation primary), D2.SD2.C1 (eol_infra_exploitation secondary),
// D2.SD2.C2 (eol_infra_exploitation secondary), D2.SD2.C3 (eol_infra_exploitation secondary),
// D2.SD3.C1 (cloud_onprem_hybrid_gap secondary), D3.SD1.C1 (agent_identity_sprawl_shadow_agents
// primary; credential_phishing_token_theft, deepfake_vishing_social_engineering secondary),
// D3.SD1.C2 (flat_network_lateral_movement, multi_vendor_visibility_gaps secondary),
// D3.SD2.C1 (credential_phishing_token_theft, deepfake_vishing_social_engineering,
// unmanaged_byod_collab_exposure primary; sdwan_control_plane_exposure, exposed_mgmt_interfaces,
// agent_prompt_injection_tool_abuse, agent_identity_sprawl_shadow_agents secondary), D3.SD2.C2
// (supply_chain_oss_propagation, regulated_data_exposure_infra primary), D3.SD2.C3
// (flat_network_lateral_movement primary; rogue_wireless_access, firewall_misconfig_exposure,
// regulated_data_exposure_infra secondary), D3.SD2.C4 (agent_prompt_injection_tool_abuse
// primary; agent_identity_sprawl_shadow_agents secondary), D3.SD3.C1 (agentic_soc_alert_fatigue
// primary; machine_speed_cve, credential_phishing_token_theft, deepfake_vishing_social_engineering,
// multi_vendor_visibility_gaps secondary).

export type ThreatId = (typeof THREATS)[number]["id"];

export const THREAT_CATEGORY_LABELS: Record<string, string> = categoryLabelsFrom();
